import { useQuery } from '@tanstack/react-query';
import { Device, DeviceType, Gateway, Beacon } from '@ixicare/resources';
import { Panel, Search } from '@ixicare/ui';
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GenericTable } from './GenericTable';
import { Spacer } from '~components/Utils/Spacer';
import { useStores } from '~store/utils';
import { mapDBGWithType } from '~modules/devices/utils/deviceMapping';
import { useDeviceDetail } from '~modules/sites/hooks/useDeviceDetail';
import { useDeviceArchive } from '~modules/sites/hooks/useDeviceArchive';
import { DBGWithType } from '~modules/devices/components/DeviceHealthTable/types';
import { useTableRowActions } from './hooks/useTableRowActions';
import {
  navigateMapAction,
  editAction,
  deleteAction,
  configAction,
  ConfigurableDevice,
  moveAction,
} from './deviceTableActions';
import {
  transformButtonToRow,
  transformWearableToRow,
  transformEviewToRow,
  transformGatewayToRow,
  transformBeaconToRow,
  BUTTON_COLUMNS_WITH_TYPE_AND_ARCHIVE,
  WEARABLE_COLUMNS_WITH_TYPE_AND_ARCHIVE,
  EVIEW_COLUMNS_WITH_TYPE_AND_ARCHIVE,
  GATEWAY_COLUMNS_WITH_TYPE_AND_ARCHIVE,
  BEACON_COLUMNS_WITH_TYPE_AND_ARCHIVE,
} from './rowTransformers';
import { BaseTableConfig } from './types';
import { DeleteDeviceModal } from '~modules/devices/components/DeviceModals/DeleteDeviceModal';
import { UpdateDeviceModal } from '~modules/devices/components/DeviceModals/UpdateDeviceModal';

type SearchResult = {
  type: 'device' | 'gateway' | 'beacon';
  data: any;
  isArchived?: boolean;
  siteId?: number;
};

async function searchByQuery(query: string): Promise<SearchResult | null> {
  // IMEI identifies cellular devices (eview/twig) — they aren't found by MAC.
  if (isImeiNumber(query)) {
    try {
      const deviceExists = await Device.deviceImeiNumberExist(query);
      if (deviceExists.id) {
        const device = await Device.getDevice(deviceExists.id, true);
        return {
          type: 'device',
          data: device,
          isArchived: deviceExists.isArchived,
          siteId: deviceExists.siteId,
        };
      }
    } catch {
      // Device not found
    }
    return null;
  }

  // Try device first
  try {
    const deviceExists = await Device.deviceMacAddressExist(query);
    if (deviceExists.id) {
      const device = await Device.getDevice(deviceExists.id, true);
      return {
        type: 'device',
        data: device,
        isArchived: deviceExists.isArchived,
        siteId: deviceExists.siteId,
      };
    }
  } catch {
    // Device not found, continue
  }

  // Try gateway next
  try {
    const gatewayExists = await Gateway.gatewayMacAddressExist(query);
    if (gatewayExists.id) {
      const gateway = await Gateway.getGateway(gatewayExists.id, true);
      return {
        type: 'gateway',
        data: gateway,
        isArchived: gatewayExists.isArchived,
        siteId: gatewayExists.siteId,
      };
    }
  } catch {
    // Gateway not found, continue
  }

  // Try beacon last
  try {
    const beaconExists = await Beacon.beaconMacAddressExist(query);
    if (beaconExists.id) {
      const beacon = await Beacon.getBeacon(beaconExists.id, true);
      return {
        type: 'beacon',
        data: beacon,
        isArchived: beaconExists.isArchived,
        siteId: beaconExists.siteId,
      };
    }
  } catch {
    // Beacon not found
  }

  return null;
}

function isMacAddress(value: string) {
  return /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/.test(value);
}

function isImeiNumber(value: string) {
  // IMEI is 15 digits (IMEISV 16); allow a small range so the lookup fires on
  // any all-digit identifier without triggering on half-typed input.
  return /^\d{14,17}$/.test(value);
}

const SEARCH_PARAM = 'q';

export function DeviceFinderTable({ siteId }: { siteId?: number }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { sites, device: deviceStore, notifications } = useStores();

  const [query, setQuery] = React.useState(() => searchParams.get(SEARCH_PARAM) ?? '');
  const isValid = isMacAddress(query) || isImeiNumber(query);

  // Sync the query into the URL so a search can be hard-linked. setSearchParams
  // strips the tab hash, so restore it the same way the archive filter does.
  const handleSearchChange = (value: string) => {
    setQuery(value);
    const currentHash = window.location.hash;
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(SEARCH_PARAM, value);
    } else {
      newParams.delete(SEARCH_PARAM);
    }
    setSearchParams(newParams, { replace: true });
    if (currentHash) {
      window.history.replaceState(null, '', window.location.pathname + '?' + newParams.toString() + currentHash);
    }
  };

  const { data, refetch } = useQuery({
    queryKey: ['search', query],
    queryFn: () => searchByQuery(query!),
    enabled: isValid,
  });

  // Get the site name if device is found in a different site
  const foundInOtherSite = React.useMemo(() => {
    if (!data || !data.siteId || !siteId) return null;
    if (data.siteId === siteId) return null;

    const site = sites.sites.find((s) => s.id === data.siteId);
    return site ? site.name : null;
  }, [data, siteId, sites.sites]);

  // Site uuid of the device itself (may differ from the selected site) — needed
  // for the map action which is scoped by site.
  const deviceSiteUuid = React.useMemo(() => {
    if (!data?.siteId) return undefined;
    return sites.sites.find((s) => s.id === data.siteId)?.uuid;
  }, [data, sites.sites]);

  // Dynamic columns based on search result type
  const columns = React.useMemo(() => {
    if (!data) return [];

    if (data.type === 'device') {
      if (data.data.deviceType === DeviceType.WALL_MOUNTED_DISTRESS_BUTTON) {
        return BUTTON_COLUMNS_WITH_TYPE_AND_ARCHIVE;
      }
      if ([DeviceType.EVIEW, DeviceType.TWIG].includes(data.data.deviceType)) {
        return EVIEW_COLUMNS_WITH_TYPE_AND_ARCHIVE;
      }
      return WEARABLE_COLUMNS_WITH_TYPE_AND_ARCHIVE;
    } else if (data.type === 'gateway') {
      return GATEWAY_COLUMNS_WITH_TYPE_AND_ARCHIVE;
    } else if (data.type === 'beacon') {
      return BEACON_COLUMNS_WITH_TYPE_AND_ARCHIVE;
    }
    return [];
  }, [data]);

  const rows = React.useMemo(() => {
    if (!data) return [];

    const context = {
      isArchived: data.isArchived,
      siteId: data.siteId,
      showArchiveColumn: true,
      showTypeColumn: true,
    };

    if (data.type === 'device') {
      if (data.data.deviceType === DeviceType.WALL_MOUNTED_DISTRESS_BUTTON) {
        return [transformButtonToRow(data.data, context)];
      }
      if ([DeviceType.EVIEW, DeviceType.TWIG].includes(data.data.deviceType)) {
        return [transformEviewToRow(data.data, context)];
      }
      return [transformWearableToRow(data.data, context)];
    } else if (data.type === 'gateway') {
      return [transformGatewayToRow(data.data, context)];
    } else if (data.type === 'beacon') {
      return [transformBeaconToRow(data.data, context)];
    }
    return [];
  }, [data]);

  // Single-row equivalent of the device tables' rows, so the shared row actions
  // (map / config / edit / archive) can operate on the found device.
  const devicesWithType = React.useMemo(() => {
    if (!data) return [];
    const type =
      data.type === 'gateway'
        ? DeviceType.GATEWAY
        : data.type === 'beacon'
          ? DeviceType.BEACON
          : (data.data.deviceType as DeviceType);
    return mapDBGWithType([data.data], type);
  }, [data]);

  const { selectedDevices, setSelectedDevices, selectedDeviceDetail, handleCloseDetail } =
    useDeviceDetail(devicesWithType);

  const { devicesToArchive, setDevicesToArchive, deleteMutation } = useDeviceArchive({
    onRefresh: refetch,
    notifications,
  });

  const onConfigAction = (devices: ConfigurableDevice[]) => {
    const dbg = devices[0];
    if (!dbg) return;
    deviceStore.hydrate({
      macAddress: dbg.macAddress,
      deviceType: dbg.type,
    });
    navigate(`/device-overview/${dbg.uuid}/configuration`);
  };

  const openDeviceModal = (dbgs: DBGWithType[]) => setSelectedDevices(dbgs);

  const { actionProps, onActionClick, isActionVisible } = useTableRowActions(
    devicesWithType,
    !data || data.isArchived
      ? [moveAction(openDeviceModal)]
      : [
          ...(data.type === 'device'
            ? [
                ...(data.data.deviceType === DeviceType.WALL_MOUNTED_DISTRESS_BUTTON
                  ? [navigateMapAction(navigate, deviceSiteUuid, false, (dbg: DBGWithType) => dbg.type)]
                  : []),
                configAction(onConfigAction),
              ]
            : [navigateMapAction(navigate, deviceSiteUuid, false, (dbg: DBGWithType) => dbg.type)]),
          editAction(openDeviceModal),
          deleteAction((dbgs) => setDevicesToArchive(dbgs)),
        ]
  );

  const tableConfig: BaseTableConfig = {
    tableId: 'device-finder',
    columns,
    columnIds: [],
  };

  return (
    <>
      <Search
        placeholder="MAC address or IMEI, e.g. EA:89:89:84:14:2F"
        onSearchChange={handleSearchChange}
        value={query}
      />
      {rows.length != 0 ? (
        <>
          <Spacer size={35} />
          {foundInOtherSite && (
            <>
              <Panel.Title>Found in other site: {foundInOtherSite}</Panel.Title>
              <Spacer size={15} />
            </>
          )}
          <GenericTable
            config={tableConfig}
            rows={rows ?? []}
            actions={actionProps}
            onActionClick={onActionClick}
            isActionVisible={isActionVisible}
          />
        </>
      ) : (
        <></>
      )}
      {selectedDevices && selectedDeviceDetail && (
        <UpdateDeviceModal
          type={selectedDeviceDetail.type}
          devices={selectedDevices}
          onClose={handleCloseDetail}
          onRefresh={refetch}
          showSiteSelect={true}
        />
      )}
      {devicesToArchive.length > 0 && (
        <DeleteDeviceModal
          devices={devicesToArchive}
          onClose={() => setDevicesToArchive([])}
          onDelete={() => deleteMutation.mutate()}
          isPending={deleteMutation.isPending}
        />
      )}
    </>
  );
}
